import { useState, useEffect, useMemo, ReactNode } from 'react';
import { DateTime } from 'luxon';
import { useSettingsStore } from '@core/store';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { MenuData } from '@core/data/menuData';
import { usePageActionContext } from '@core/contexts/PageActionContext.tsx';
import { PageAction } from '@core/pubsub/EventBus';

interface RouteItem {
    path: string;
}

interface NewPageTitleBarProps {
    icon: ReactNode;
    title: string;
    menus?: MenuData;
    currentRoute?: RouteItem;
    t: (key: string) => string;
    children?: ReactNode;
    actions?: ReactNode;
    rightContent?: ReactNode;
    iconSize?: string;
    hasAutoRefresh?: boolean;
    lastUpdate?: DateTime;
    showBorder?: boolean;
}

const findMenuItem = (menuItems: any[], resolvedPath: string): any => {
    if (!menuItems) return null;
    for (const menu of menuItems) {
        if (menu?.path?.localeCompare(resolvedPath, undefined, { sensitivity: 'base' }) === 0) {
            return menu;
        }
        const found = findMenuItem(menu.items ?? [], resolvedPath);
        if (found) return found;
    }
    return null;
};

const NewPageTitleBar: React.FC<NewPageTitleBarProps> = ({
    icon,
    title,
    menus,
    currentRoute,
    t,
    children,
    actions,
    rightContent,
    iconSize,
    hasAutoRefresh = false,
    lastUpdate,
    showBorder = true
}) => {
    const { selectedLocale, isMobile } = useSettingsStore();
    const [formattedTime, setFormattedTime] = useState<string>('');

    // Read per-action state from context
    // Each getAction call returns { hasAction, isRunning, isDisabled, trigger }.
    // If a page didn't register a given action the button simply won't render.
    const { getAction } = usePageActionContext();
    const refresh = getAction(PageAction.REFRESH);

    // Add future actions here:
    // const sync = getAction(PageAction.SYNC);

    useEffect(() => {
        if (lastUpdate) {
            const formatted = lastUpdate.setLocale(selectedLocale.locale).toLocaleString(DateTime.TIME_24_WITH_SECONDS);
            setFormattedTime(formatted);
        }
    }, [lastUpdate, selectedLocale.locale]);

    const description = useMemo(() => {
        if (!menus || !currentRoute?.path) return '';
        const menuValues = Object.values(menus);
        const menuItem = findMenuItem(menuValues, currentRoute.path);
        return menuItem?.description ?? '';
    }, [menus, currentRoute]);

    const hasLeftContent = !!icon || !!title;

    if (isMobile) {
        if (!refresh.hasAction) return null;

        return (
            <Box
                className="new-page-title-bar-mobile"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {hasAutoRefresh && formattedTime && (
                        <Box
                            component="img"
                            src="/images/icons/repeat-1.svg"
                            alt="refresh"
                            sx={{
                                width: 16,
                                height: 16,
                                filter: 'var(--gray-dark)'
                            }}
                        />
                    )}
                    <Typography
                        sx={{
                            fontSize: '12px',
                            letterSpacing: '1.2px',
                            color: 'var(--gray-dark)'
                        }}
                    >
                        {formattedTime ? `${t('lastUpdate')}: ${formattedTime}` : ''}
                    </Typography>
                </Box>
                <Button
                    variant="text"
                    startIcon={
                        refresh.isRunning ? (
                            <CircularProgress size={14} sx={{ color: 'var(--main-blue)' }} />
                        ) : (
                            <i className="bi bi-arrow-repeat" style={{ fontSize: '16px' }} />
                        )
                    }
                    disabled={refresh.isRunning || refresh.isDisabled}
                    onClick={refresh.trigger}
                    sx={{ textTransform: 'none', fontSize: '12px', color: 'var(--main-blue)' }}
                >
                    {t('refresh')}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 3 }}>
            <Box
                className="new-page-title-bar-root"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    px: hasLeftContent ? 4 : 2,
                    pt: hasLeftContent ? 2 : 1,
                    pb: 2,
                    minHeight: hasLeftContent ? '68px' : 'auto'
                }}
            >
                {/* Row 1: icon + title + children + rightContent + last update + action buttons + help */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '56px' }}>
                    {hasLeftContent && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {icon && (
                                <Box
                                    className="new-page-title-bar-icon"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        lineHeight: 1,
                                        '& i': {
                                            fontSize: `${iconSize ?? '16px'} !important`,
                                            color: '#000000 !important'
                                        },
                                        marginBottom: 0.1
                                    }}
                                >
                                    {icon}
                                </Box>
                            )}
                            {title && (
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        letterSpacing: '0.05em',
                                        fontFamily: 'var(--font-family-medium)',
                                        color: '#000000'
                                    }}
                                >
                                    {title.toUpperCase()}
                                </Typography>
                            )}

                            {description && (
                                <Tooltip
                                    title={t(description)}
                                    placement="right"
                                    arrow
                                    classes={{ tooltip: 'page-title-tooltip' }}
                                >
                                    <IconButton
                                        size="small"
                                        sx={{ '& i': { fontSize: '16px !important', color: 'var(--gray-medium)' } }}
                                    >
                                        <i className="bi bi-question-circle" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '44px' }}>
                        {children}
                        {rightContent}

                        {/* Last update timestamp — shown when refresh action exists */}
                        {refresh.hasAction && formattedTime && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {hasAutoRefresh && (
                                    <Box
                                        component="img"
                                        src="/images/icons/repeat-1.svg"
                                        alt="refresh"
                                        sx={{
                                            width: 16,
                                            height: 16,
                                            filter: 'var(--gray-dark)'
                                        }}
                                    />
                                )}
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        letterSpacing: '1.2px',
                                        fontWeight: 400,
                                        fontStyle: 'normal',
                                        leadingTrim: 'both',
                                        textEdge: 'cap',
                                        color: 'var(--gray-dark)'
                                    }}
                                >
                                    {t('lastUpdate')}: {formattedTime}
                                </Typography>
                            </Box>
                        )}

                        {/* Refresh button — renders only when page registered REFRESH */}
                        {refresh.hasAction && (
                            <Button
                                variant="text"
                                startIcon={
                                    refresh.isRunning ? (
                                        <CircularProgress size={14} sx={{ color: 'var(--main-blue)' }} />
                                    ) : (
                                        <i className="bi bi-arrow-repeat" style={{ fontSize: '16px' }} />
                                    )
                                }
                                disabled={refresh.isRunning || refresh.isDisabled}
                                onClick={refresh.trigger}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '12px',
                                    color: 'var(--main-blue)',
                                    '& .MuiSvgIcon-root': {
                                        color: 'var(--main-blue)'
                                    }
                                }}
                            >
                                {t('refresh')}
                            </Button>
                        )}
                    </Box>
                </Box>
                {/* Row 2: actions (buttons, time info) */}
                {actions && <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>{actions}</Box>}
                {showBorder && (
                    <Box
                        className="new-page-title-bar-border"
                        sx={{
                            height: '1px',
                            bgcolor: 'var(--sidebar-divider)',
                            mx: -0.85
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default NewPageTitleBar;
